import type { ClinicLearner, Immunization, MedicalRecord, MedicalVisit, Medication, Prescription, VitalSigns } from "../../services/medicalService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The sick bay's registers -- visits, health records, prescriptions, the
 * medicine store and immunisations -- and the small clinical rules the
 * screens explain (what is due, what is running low, what to warn about).
 * Choices mirror the server's model choices in apps/medical/models.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");
const DAY_MS = 86_400_000;
/** Whole days from `from` to `to`, both yyyy-mm-dd. */
export const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);

export const VISIT_TYPES: [string, string][] = [
  ["sick_visit", "Sick visit"],
  ["injury", "Injury"],
  ["emergency", "Emergency"],
  ["routine", "Routine check-up"],
  ["follow_up", "Follow-up"],
  ["vaccination", "Vaccination"],
  ["mental_health", "Mental health"],
  ["other", "Other"],
];
export const PRIORITIES: [string, string][] = [
  ["low", "Low"],
  ["medium", "Medium"],
  ["high", "High"],
  ["urgent", "Urgent"],
];
export const MEDICATION_TYPES: [string, string][] = [
  ["tablet", "Tablet"],
  ["capsule", "Capsule"],
  ["syrup", "Syrup"],
  ["injection", "Injection"],
  ["cream", "Cream"],
  ["drops", "Drops"],
  ["inhaler", "Inhaler"],
  ["other", "Other"],
];
export const VACCINE_TYPES: [string, string][] = [
  ["bcg", "BCG"],
  ["dpt", "DPT"],
  ["polio", "Polio"],
  ["measles", "Measles"],
  ["mmr", "MMR"],
  ["hepatitis_b", "Hepatitis B"],
  ["tetanus", "Tetanus"],
  ["typhoid", "Typhoid"],
  ["yellow_fever", "Yellow fever"],
  ["meningitis", "Meningitis"],
  ["covid_19", "COVID-19"],
  ["other", "Other"],
];
export const REACTION_SEVERITIES: [string, string][] = [
  ["none", "None"],
  ["mild", "Mild"],
  ["moderate", "Moderate"],
  ["severe", "Severe"],
];
export const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
/** The stock movements a person records by hand; dispensing and opening stock are recorded by the server. */
export const STOCK_MOVES: { value: string; label: string; sign: 1 | -1 | 0; hint: string }[] = [
  { value: "return", label: "Received or returned", sign: 1, hint: "Adds to the stock." },
  { value: "expired", label: "Expired, thrown away", sign: -1, hint: "Takes away from the stock." },
  { value: "damage", label: "Damaged or lost", sign: -1, hint: "Takes away from the stock." },
  { value: "adjustment", label: "Count correction", sign: 0, hint: "Type a minus sign to take away, e.g. -5." },
];
export const STOCK_LABELS: Record<string, string> = {
  opening: "Opening stock",
  adjustment: "Count correction",
  dispense: "Dispensed",
  return: "Received / returned",
  expired: "Expired",
  damage: "Damaged or lost",
};

export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);
const has = (value?: string | null) => Boolean(value && value.trim() && !/^(none|nil|n\/a|no|-)\.?$/i.test(value.trim()));

// --- Learners ------------------------------------------------------------------------------

export const learnerLine = (learner: Pick<ClinicLearner, "student_id" | "class_name">) =>
  [learner.class_name, learner.student_id].filter(Boolean).join(" · ");

/** Learners in school order (class, then name) for the pickers. */
export const sortLearners = (learners: ClinicLearner[]) =>
  [...learners].sort((a, b) => (a.class_level_order ?? 999) - (b.class_level_order ?? 999) || (a.class_name ?? "").localeCompare(b.class_name ?? "") || a.full_name.localeCompare(b.full_name));

/** What a clinician must see before treating or prescribing: allergies and long-term conditions. */
export function clinicalAlerts(record?: Pick<MedicalRecord, "allergies" | "chronic_conditions" | "medications" | "physical_limitations"> | null) {
  if (!record) return [];
  const alerts: { label: string; text: string; tone: "danger" | "warning" }[] = [];
  if (has(record.allergies)) alerts.push({ label: "Allergies", text: record.allergies!.trim(), tone: "danger" });
  if (has(record.chronic_conditions)) alerts.push({ label: "Long-term conditions", text: record.chronic_conditions!.trim(), tone: "warning" });
  if (has(record.medications)) alerts.push({ label: "Regular medicines", text: record.medications!.trim(), tone: "warning" });
  if (has(record.physical_limitations)) alerts.push({ label: "Physical limitations", text: record.physical_limitations!.trim(), tone: "warning" });
  return alerts;
}

// --- Visits --------------------------------------------------------------------------------

export const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
export const priorityDot = (priority: string) =>
  priority === "urgent" ? "bg-red-500" : priority === "high" ? "bg-amber-500" : priority === "medium" ? "bg-sky-500" : "bg-gray-400";
export const isOpenVisit = (visit: Pick<MedicalVisit, "status">) => visit.status === "scheduled" || visit.status === "in_progress";

export function visitStatus(visit: Pick<MedicalVisit, "status" | "visit_date">, today: string) {
  switch (visit.status) {
    case "in_progress":
      return { label: "With the clinician", dot: "bg-sky-500" };
    case "completed":
      return { label: "Seen", dot: "bg-emerald-500" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    default:
      if (visit.visit_date < today) return { label: "Not seen", dot: "bg-red-500" };
      if (visit.visit_date === today) return { label: "Waiting", dot: "bg-amber-500" };
      return { label: "Booked", dot: "bg-indigo-500" };
  }
}

/** A seen visit whose follow-up falls due within the week (or already has). */
export const followUpDue = (visit: Pick<MedicalVisit, "status" | "follow_up_required" | "follow_up_date">, today: string) =>
  visit.status === "completed" && visit.follow_up_required && Boolean(visit.follow_up_date) && daysBetween(today, visit.follow_up_date!) <= 7;

export const visitConfig = (today: string): RegisterConfig<MedicalVisit> => ({
  search: (row) => [row.student_name, row.student_id, row.chief_complaint, row.symptoms, row.diagnosis, row.medical_staff_name].join(" "),
  views: [
    { key: "today", label: "Today", match: (row) => row.visit_date === today },
    { key: "open", label: "Waiting or open", match: isOpenVisit },
    { key: "follow-up", label: "Follow-up due", match: (row) => followUpDue(row, today) },
    { key: "seen", label: "Seen", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Visit date", value: (row) => row.visit_date },
  category: { label: "Kind of visit", value: (row) => labelFrom(VISIT_TYPES, row.visit_type) },
  sorts: [
    {
      key: "queue",
      label: "Most urgent first",
      compare: (a, b) =>
        Number(isOpenVisit(b)) - Number(isOpenVisit(a)) ||
        (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
        `${b.visit_date}${b.visit_time}`.localeCompare(`${a.visit_date}${a.visit_time}`),
    },
    { key: "-when", label: "Newest first", compare: reversed(byText<MedicalVisit>((row) => `${row.visit_date}${row.visit_time}`)) },
    { key: "when", label: "Oldest first", compare: byText<MedicalVisit>((row) => `${row.visit_date}${row.visit_time}`) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<MedicalVisit>((row) => row.student_name) },
  ],
});

/** The vital signs as one line, in the order a clinician reads them. */
export function vitalsLine(vitals?: VitalSigns | null) {
  if (!vitals) return "";
  return [
    vitals.temperature && `${vitals.temperature} °C`,
    vitals.pulse_rate && `pulse ${vitals.pulse_rate}/min`,
    vitals.respiratory_rate && `breathing ${vitals.respiratory_rate}/min`,
    vitals.blood_pressure && `BP ${vitals.blood_pressure}`,
    vitals.oxygen_saturation && `SpO₂ ${vitals.oxygen_saturation}%`,
    vitals.weight && `${vitals.weight} kg`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** A temperature a school clinic treats as fever. */
export const isFever = (vitals?: VitalSigns | null) => Number(vitals?.temperature) >= 37.5;

// --- Health records -------------------------------------------------------------------------

export const recordConfig: RegisterConfig<MedicalRecord> = {
  search: (row) => [row.student_name, row.student_id, row.allergies, row.chronic_conditions, row.medications, row.emergency_contact_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "allergies", label: "Allergies", match: (row) => has(row.allergies) },
    { key: "conditions", label: "Long-term conditions", match: (row) => has(row.chronic_conditions) },
    { key: "immunisation", label: "Immunisation incomplete", match: (row) => !row.immunization_complete },
    { key: "no-contact", label: "No emergency contact", match: (row) => !has(row.emergency_contact_phone) },
  ],
  category: { label: "Blood group", value: (row) => row.blood_type || "Not recorded" },
  sorts: [
    { key: "learner", label: "Learner (A–Z)", compare: byText<MedicalRecord>((row) => row.student_name) },
    { key: "-updated", label: "Recently updated", compare: reversed(byText<MedicalRecord>((row) => row.last_updated ?? "")) },
  ],
};

export const recordFlags = (record: MedicalRecord) =>
  [has(record.allergies) && "Allergies", has(record.chronic_conditions) && "Condition", !has(record.emergency_contact_phone) && "No contact"].filter(Boolean) as string[];

// --- Prescriptions --------------------------------------------------------------------------

export function prescriptionStatus(row: Pick<Prescription, "status">) {
  switch (row.status) {
    case "prescribed":
      return { label: "To dispense", dot: "bg-amber-500" };
    case "dispensed":
      return { label: "On the course", dot: "bg-sky-500" };
    case "completed":
      return { label: "Course finished", dot: "bg-emerald-500" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const prescriptionConfig: RegisterConfig<Prescription> = {
  search: (row) => [row.student_name, row.student_id, row.medication_name, row.dosage, row.instructions, row.prescribed_by_name].join(" "),
  views: [
    { key: "to-dispense", label: "To dispense", match: (row) => row.status === "prescribed" },
    { key: "on-course", label: "On the course", match: (row) => row.status === "dispensed" },
    { key: "finished", label: "Finished", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Prescribed", value: (row) => row.prescribed_date },
  category: { label: "Medicine", value: (row) => row.medication_name },
  sorts: [
    { key: "-prescribed", label: "Newest first", compare: reversed(byText<Prescription>((row) => `${row.prescribed_date}${String(row.id).padStart(8, "0")}`)) },
    { key: "prescribed", label: "Oldest first", compare: byText<Prescription>((row) => `${row.prescribed_date}${String(row.id).padStart(8, "0")}`) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<Prescription>((row) => row.student_name) },
    { key: "medicine", label: "Medicine (A–Z)", compare: byText<Prescription>((row) => row.medication_name) },
  ],
};

/** Why a prescription cannot be dispensed now, or "" when it can. */
export function dispenseBlock(prescription: Pick<Prescription, "quantity">, medicine: Pick<Medication, "current_stock" | "expiry_date" | "is_active"> | undefined, today: string) {
  if (!medicine) return "The medicine is no longer in the store list.";
  if (!medicine.is_active) return "The medicine is no longer stocked.";
  if (medicine.expiry_date && medicine.expiry_date < today) return `The stock expired on ${medicine.expiry_date}.`;
  if (medicine.current_stock < prescription.quantity) return `Only ${medicine.current_stock} in stock; ${prescription.quantity} needed.`;
  return "";
}

// --- Medicine store -------------------------------------------------------------------------

export const EXPIRY_WARNING_DAYS = 60;

export function medicineStatus(medicine: Pick<Medication, "current_stock" | "minimum_stock" | "expiry_date">, today: string) {
  if (medicine.expiry_date && medicine.expiry_date < today) return { label: "Expired", dot: "bg-red-500", alert: true };
  if (medicine.current_stock <= 0) return { label: "Out of stock", dot: "bg-red-500", alert: true };
  if (medicine.current_stock <= medicine.minimum_stock) return { label: "Running low", dot: "bg-amber-500", alert: true };
  if (medicine.expiry_date && daysBetween(today, medicine.expiry_date) <= EXPIRY_WARNING_DAYS) return { label: "Expires soon", dot: "bg-amber-500", alert: true };
  return { label: "In stock", dot: "bg-emerald-500", alert: false };
}

export const medicineName = (medicine: Pick<Medication, "name" | "strength">) => [medicine.name, medicine.strength].filter(Boolean).join(" ");

export const medicineConfig = (today: string): RegisterConfig<Medication> => ({
  search: (row) => [row.name, row.generic_name, row.strength, row.dosage_form, row.indications].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "low", label: "Low or out", match: (row) => row.current_stock <= row.minimum_stock },
    {
      key: "expiry",
      label: "Expired or expiring",
      match: (row) => Boolean(row.expiry_date) && daysBetween(today, row.expiry_date!) <= EXPIRY_WARNING_DAYS,
    },
    { key: "prescription", label: "Prescription only", match: (row) => row.requires_prescription },
  ],
  category: { label: "Form", value: (row) => labelFrom(MEDICATION_TYPES, row.medication_type) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<Medication>((row) => medicineName(row)) },
    { key: "stock", label: "Lowest stock first", compare: byNumber<Medication>((row) => row.current_stock - row.minimum_stock) },
    { key: "expiry", label: "Expires soonest", compare: byText<Medication>((row) => row.expiry_date || "9999") },
  ],
});

/** The signed quantity the server will record for a hand-entered stock movement. */
export function signedQuantity(move: string, quantity: number) {
  const sign = STOCK_MOVES.find((entry) => entry.value === move)?.sign ?? 0;
  return sign === 0 ? quantity : sign * Math.abs(quantity);
}

// --- Immunisations --------------------------------------------------------------------------

export const DOSE_WARNING_DAYS = 30;

export function nextDoseStatus(row: Pick<Immunization, "next_dose_due">, today: string) {
  if (!row.next_dose_due) return { label: "No further dose", dot: "bg-gray-300" };
  const days = daysBetween(today, row.next_dose_due);
  if (days < 0) return { label: `Overdue since ${row.next_dose_due}`, dot: "bg-red-500" };
  if (days === 0) return { label: "Due today", dot: "bg-amber-500" };
  if (days <= DOSE_WARNING_DAYS) return { label: `Due in ${days} ${days === 1 ? "day" : "days"}`, dot: "bg-amber-500" };
  return { label: `Due ${row.next_dose_due}`, dot: "bg-emerald-500" };
}

export const doseDueSoon = (row: Pick<Immunization, "next_dose_due">, today: string) =>
  Boolean(row.next_dose_due) && daysBetween(today, row.next_dose_due!) <= DOSE_WARNING_DAYS;

export const immunisationConfig = (today: string): RegisterConfig<Immunization> => ({
  search: (row) => [row.student_name, row.student_id, row.vaccine_name, row.batch_number, row.manufacturer, row.administered_by_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "due", label: "Next dose due", match: (row) => doseDueSoon(row, today) },
    { key: "reactions", label: "Reactions", match: (row) => row.reaction_severity !== "none" || has(row.adverse_reactions) },
  ],
  date: { label: "Given", value: (row) => row.vaccination_date },
  category: { label: "Vaccine", value: (row) => labelFrom(VACCINE_TYPES, row.vaccine_type) },
  sorts: [
    { key: "-given", label: "Newest first", compare: reversed(byText<Immunization>((row) => row.vaccination_date)) },
    { key: "next", label: "Next dose soonest", compare: byText<Immunization>((row) => row.next_dose_due || "9999") },
    { key: "learner", label: "Learner (A–Z)", compare: byText<Immunization>((row) => row.student_name) },
  ],
});
